import {autoinject, transient, computedFrom, BindingEngine, useView} from "aurelia-framework";
import { I18N } from "aurelia-i18n";
import { DataType, DataGridColumn, SourceType, QueryFilter } from "../../../../../../../models/queries/queries.models";
import { MobileHelper } from "../../../../../../../helpers/mobile-helper";
import * as moment from "moment";
import { CustomGridPageBase } from "../../../../../common/custom-grid-page.base";
import { RatingItem } from "../../../../../../components/rating-selector/rating-selector";
import * as Enumerable from "linq";
import { UserClaimsService } from "../../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../../constants/authentication/claims";
import { Guid } from "../../../../../../../utilities/guid";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { VesselHelper } from "../../../../../../../models/vessels/vessel.models";
import { RightShipDialogServiceConstants } from "../../../../../../../constants/basics/rightship-dialog.constants";
import { MultiLookupModel, RightShipMultiLookupModels } from "../../../../../../components/multi-checked-list-selector/multi-checked-list-selector";
import { LookupValueQuery } from "../../../../../../../constants/modules/lookup.constants";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { LookupClientService } from "../../../../../../../services/lookups/lookup-client.service";
import { LookupValueRequest } from "../../../../../../../models/lookups/lookup.models";
import { RangeSelectorHelper } from "../../../../../../components/range-selector/range-selector";
import { Reflection } from "../../../../../../../utilities/reflection";

@autoinject
@transient()
@useView('views/pages/admin/lists/list-items-wrapper/steps/psc-performance/psc-performance.html')
export class PscPerformance extends CustomGridPageBase {
    companyId: number = null;
    listId: number = null;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];

    public isAccessedFromLists: boolean;

    constructor(i18N: I18N, protected bindingEngine: BindingEngine, userClaimsService: UserClaimsService, private dialogClientService: DialogClientService, private lookupService: LookupClientService) {
        super(i18N, 'Vessels', bindingEngine);

        this.rightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);
    }

    activate(model: { companyId: number, listId: number }) {
        if (model.companyId)
            this.companyId = model.companyId;
        if (model.listId)
            this.listId = model.listId;
        this.columns = [];

        this.columns = [];

        this.columns.push(
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<a class="link" href="/vessel/${item.vesselId}">${item.name}</a>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span>' +
                    '</div>',
                name: "name",
                title: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                }
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item"" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType"'+
                    'has-error.bind="item.hasError" is-condensed="true" incident-grace-periods.bind="getIncidentGracePeriods(item)"></comp-safety-score>',
                name: "safetyRating",
                title: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "Safety Score",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "220px"
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-ghg-rating rating.bind="item.ghgRating" rating-plus.bind="item.isGhgRatingPlus" is-verified.bind="item.isGhgRatingVerified"></comp-ghg-rating>',
                name: "ghgRatingWeight",
                title: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "GHG Rating",
                instancePerRow: true,
                sortOrder: 0,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null
                },
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "140px"
            },
            <DataGridColumn>{
                sortOrder: 1,
                columnOrder: 1,
                name: "vesselType",
                dataType: DataType.Default,
                title: "Vessel Type",
                isSortable: true,
                isFilterable: true
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "deadWeightTonnage",
                title: "DWT",
                sortOrder: 2,
                columnOrder: 2,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Default,
                isSortable: true,
                name: 'vesselStatus',
                title: 'Vessel Status'
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "pscInspectionCount",
                title: "Insp",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "detentions",
                title: "Det",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "totalDeficiencies",
                title: "Defs",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "60px"
            },
            <DataGridColumn>{
                dataType: DataType.Number,
                name: "averageDeficienciesPerInspection",
                title: "Avg def/insp",
                sortOrder: 5,
                columnOrder: 5,
                isHidden: false,
                isSortable: true,
                width: MobileHelper.isMobileDevice()  &&  window.innerWidth < 1024 ? "" : "120px",
                valueConverter: (x) => x.toFixed(1)
            },
            <DataGridColumn>{
                name: "beneficialOwnerCompanyId",
                title: "Roles",
                sortOrder: -1,
                columnOrder: 9,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                isHidden: this.isAccessedFromLists,
                isSortable: false,
                disableSorting: true,
                parameter: {
                    item: null
                },
                value: "<div class='row'>" +
                    "<div class='role bo' if.bind='item.isBeneficialOwnerCompany'>BO</div>" +
                    "<div class='role cm' if.bind='item.isCommercialManagerCompany'>CM</div>" +
                    "<div class='role doc' if.bind='item.isDocumentOfComplianceCompany'>DOC</div>" +
                    "<div class='role cq' if.bind='item.isCommericalOwnerCompany'>CO</div>" +
                    "<div class='role ro' if.bind='item.isRegisteredOwnerCompany'>RO</div>" +
                    "<div class='role tm' if.bind='item.isTechnicalManagerCompany'>TM</div>" +
                    "</div>"
            });


        this.selectedPscDates = [5];

        this.setDefaultFilters();
    }

    private readonly defaultStatusGroupFilters: string[] = ["in service"];


    private setDefaultFilters() {
        this.selectedRoles = this.isAccessedFromLists ? [] : this.roles.filter(x => x.prop == "isDocumentOfComplianceCompany" || x.prop == "isTechnicalManagerCompany").map(x => x.id);
        this.mobileRoles = CloneHelper.cloneObject(this.selectedRoles);
        this.lookupService.getLookup(new LookupValueRequest(LookupValueQuery.VesselStatusGroups)).then(x => {

            const values = x.activeValues;

            if (values && values.length) {
                const lookup = LookupValueQuery.VesselStatuses;
                const ids = values.filter(v => this.defaultStatusGroupFilters.indexOf(v.name.trim().toLowerCase()) > -1).map(v => v.id);
                RightShipMultiLookupModels.setSelectedInMultiLookupByIds(LookupValueQuery.VesselStatuses, this.lookupService, ids, LookupValueQuery.VesselStatusGroups)
                    .then(res => {
                        this.mclFilters[lookup] = res.filters;
                        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text') + ' ' + res.filterDisplayText.trim();
                        this.vesselStatusDescription = res.filterDescription;
                        this.reloadFilters();
                    });
            }
        }).catch(e => {
            this.reloadFilters();
            console.error(e);
        });
    }

    getBuilt(dateOfBuilt?: Date): string {
        if (!dateOfBuilt) return '';

        const date = moment(dateOfBuilt);
        const yearAge = VesselHelper.getAgeFromDate(date);
        return yearAge;
    }

    public reloadFilters() {
        const initialFilters = [];


        //#region safety rating filter
        if (this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0) {
            const guid = Guid.generateGuid();
            const hasSafetyRating : boolean = this.selectedSafetyRatings.filter(x => x >= 0).length > 0;
            if(hasSafetyRating) {
                const safetyRatingFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRating",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: this.selectedSafetyRatings.filter(x => x >= 0),
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                initialFilters.push(safetyRatingFilter);
            }

            const isIncludeNa : boolean = this.selectedSafetyRatings.indexOf(-1) >= 0;
            if(isIncludeNa) {
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: hasSafetyRating ? false : true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };    
                initialFilters.push(naFilter);
            }
            else if (hasSafetyRating){
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1}) eq false",
                        friendlyName: "not in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };    
                initialFilters.push(naFilter);
            }
        }
        //#endregion

        //#region ghg values

        if (this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0) {

            const ghgRatingFilter: QueryFilter = {
                filterOperator: {
                    translatedSymbol: "{0} in ({1})",
                    friendlyName: "in",
                    requiresFilterValue: true,
                    requiresInterpolation: true
                },
                filterProperty: {
                    name: "ghgRatingWeight",
                    isDynamicProperty: false,
                    type: DataType.Number,
                    isNullable: true,
                },
                filterValue: this.selectedGhgRatingWeights || [],
                isAnd: true,
                skipOnNullOrEmpty: true,
            };

            initialFilters.push(ghgRatingFilter);
        }

        //#endregion

         //#region vessel types values

         if (this.vesselTypeFilterActive) 
         {
             this.mclFilters[LookupValueQuery.VesselTypes].forEach(vtf => {
                 initialFilters.push(vtf);
             });
         }
 
         //#endregion

        //#region dwt values

        if (this.dwtFirstFilter || this.dwtSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('deadWeightTonnage', this.dwtFirstFilter ? this.dwtFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('deadWeightTonnage', this.dwtSecondFilter ? this.dwtSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region insp values

        if (this.inspFirstFilter || this.inspSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('pscInspectionCount', this.inspFirstFilter ? this.inspFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('pscInspectionCount', this.inspSecondFilter ? this.inspSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region det values

        if (this.detFirstFilter || this.detSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('detentions', this.detFirstFilter ? this.detFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('detentions', this.detSecondFilter ? this.detSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region defs values

        if (this.defsFirstFilter || this.defsSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('totalDeficiencies', this.defsFirstFilter ? this.defsFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('totalDeficiencies', this.defsSecondFilter ? this.defsSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion

        //#region avgdefsinsp values

        if (this.avgDefInspFirstFilter || this.avgDefInspSecondFilter) {
            initialFilters.push(QueryFilter.getQuery('averageDeficienciesPerInspection', this.avgDefInspFirstFilter ? this.avgDefInspFirstFilter : 0, 'ge', DataType.Number, true));
            initialFilters.push(QueryFilter.getQuery('averageDeficienciesPerInspection', this.avgDefInspSecondFilter ? this.avgDefInspSecondFilter : 0, 'le', DataType.Number, true));
        }

        //#endregion
        

        //#region vessel status values

        if (this.vesselStatusFilterActive) 
        {
            this.mclFilters[LookupValueQuery.VesselStatuses].forEach(vtf => {
                initialFilters.push(vtf);
            });
        }
        //#endregion

        //#region psc date values

        let sinceMonths = 'null';

        if (this.selectedPscDates && this.selectedPscDates.length > 0) {
            sinceMonths = Enumerable.from(this.pscDates.filter(x => this.selectedPscDates.filter(y => x.id == y).length > 0).map(x => x.months)).last().toString();
        }

        //#endregion

        //#region roles values

        if (this.selectedRoles && this.selectedRoles.length > 0) {

            const selectedRoles = Enumerable.from(this.roles.filter(x => this.selectedRoles.filter(y => x.id == y).length > 0).map(x => x.prop));
            const rolesGroupGuid = Guid.generateGuid();
            selectedRoles.forEach((selectedRole, i) => {
                initialFilters.push(QueryFilter.getQuery(selectedRole, true, 'eq', DataType.Boolean, i == 0, i == (selectedRoles.count() - 1), rolesGroupGuid, true));
            });
        }

        //#endregion
        this.initialFilters = initialFilters;
        this.itemSource = {
            sourceType: SourceType.ODataUrl,
            source: "odata/CompanyPscVesselPerformances/Genesis.GetPscVesselPerformances(companyId=" + this.companyId + ",listId=" + this.listId + ", sinceMonths=" + sinceMonths + ")",
            columns: this.columns,
            initialFilters: initialFilters
        };
    }


    public closeAllFilters() {
        this.showSafetyRatingFilter = false;
        this.showGhgRatingFilter = false;
        this.showDwtFilter = false;
        this.showInspFilter = false;
        this.showDetFilter = false;
        this.showDefsFilter = false;
        this.showAvgDefInspFilter = false;
        this.showPscDateFilter = false;
        this.showRoleFilter = false;
    }

    public clearAllFilters() {
        this.selectedSafetyRatings = null;
        this.selectedGhgRatingWeights = null;
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.inspFirstFilter = null;
        this.inspSecondFilter = null;
        this.detFirstFilter = null;
        this.detSecondFilter = null;
        this.defsFirstFilter = null;
        this.defsSecondFilter = null;
        this.avgDefInspFirstFilter = null;
        this.avgDefInspSecondFilter = null;
        this.selectedPscDates = null;
        this.selectedRoles = null;

        this.avgDefInspIsApplied = false;
        this.defsIsApplied = false;
        this.detIsApplied = false;
        this.inspIsApplied = false;
        this.dwtIsApplied = false;

        this.clearVesselTypeFilter(false);
        this.clearVesselStatusFilter(false);

        this.reloadFilters();
    }

    @computedFrom(Reflection.getPropertyPath<PscPerformance>(z => z.showSafetyRatingFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showGhgRatingFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showDwtFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showInspFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showDetFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showDefsFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showAvgDefInspFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showPscDateFilter),
    Reflection.getPropertyPath<PscPerformance>(z => z.showRoleFilter))
    public get mobileFilterSelected() {
        return this.showSafetyRatingFilter || this.showGhgRatingFilter
            || this.showDwtFilter || this.showInspFilter || this.showDetFilter || this.showDefsFilter || this.showAvgDefInspFilter
            || this.showPscDateFilter || this.showRoleFilter;
    }

    //clear: clear filter value, reset: used for mobile to remove unapplied values, apply: apply the the filter values
    public selectedFilter: { title: string, clear: () => void, reset: () => void, apply: () => void };

    public goBackToFilters() {
        if (this.selectedFilter)
            this.selectedFilter.reset();
        this.closeAllFilters();
        this.selectedFilter = null;
    }

    onMobileApply() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
            this.closeAllFilters();
        } else {
            this.closeAllFilters();
            this.toggleMobileFilterDialog();
        }
    }

    //#region safety Score filter

    //safety Score filter
    public showSafetyRatingFilter: boolean = false;
    public selectedSafetyRatings: number[] = [];
    public mobileSafetyRatings: number[] = [];

    public toggleSafetyRatingFilters() {
        if (!this.showSafetyRatingFilter) {
            this.closeAllFilters();
        }
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
        this.selectedFilter = {
            title: "Safety Score",
            clear: () => {
                this.clearSafetyRatingFilter();
            },
            reset: () => {
                this.mobileSafetyRatings = CloneHelper.cloneObject(this.selectedSafetyRatings);
            },
            apply: () => {
                this.applySafetyRatingFilter(this.mobileSafetyRatings.map(r => new RatingItem(true, r, r == -1? -1 : -3))); //if rating is -1 we set the rating type to Na (-1) and if its not -1 we set it to full rating (-3)
            }
        }
    }

    public applySafetyRatingFilter(ratings: RatingItem[]) {
        this.selectedSafetyRatings = [];

        if (ratings && ratings.length > 0) {
            this.selectedSafetyRatings = this.selectedSafetyRatings.concat(ratings.map(x => x.rating));
        }

        this.reloadFilters();

        this.toggleSafetyRatingFilters();
    }

    public clearSafetyRatingFilter() {
        this.selectedSafetyRatings = [];
        this.mobileSafetyRatings = [];
        this.reloadFilters();
    }

    @computedFrom("selectedSafetyRatings", "selectedSafetyRatings.length")
    public get safetyRatingFilterActive() {
        return this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0;
    }

    //#endregion

    //#region ghg rating
    public selectedGhgRatingWeights: number[] = [];
    public mobileGhgRatingWeights: number[] = [];
    public showGhgRatingFilter: boolean = false;

    public toggleGhgRatingFilters() {
        if (!this.showGhgRatingFilter) {
            this.closeAllFilters();
        }
        this.showGhgRatingFilter = !this.showGhgRatingFilter;
        this.selectedFilter = {
            title: "GHG Rating",
            clear: () => {
                this.clearGhgRatingFilter();
            },
            reset: () => {
                this.mobileGhgRatingWeights = CloneHelper.cloneObject(this.selectedGhgRatingWeights);
            },
            apply: () => {
                this.applyGhgRating(this.mobileGhgRatingWeights);
            }
        }
    }

    public applyGhgRating(items: number[]) {
        this.selectedGhgRatingWeights = items;
        this.reloadFilters();
        this.toggleGhgRatingFilters();
    }

    public clearGhgRatingFilter() {
        this.selectedGhgRatingWeights = [];
        this.mobileGhgRatingWeights = [];
        this.reloadFilters();
    }

    @computedFrom("selectedGhgRatings", "selectedGhgRatings.length")
    public get ghgRatingFilterActive() {
        return this.selectedGhgRatingWeights && this.selectedGhgRatingWeights.length > 0;
    }

    //#endregion

    //#region Multi Checked List dialog filters
    public cachedMultipleLookups: {[lookup:string]: MultiLookupModel[]} = {};
    public mclFilters: {[lookup:string]: QueryFilter[]} = {};

    //#region vessel type filter
    public vesselTypeDisplayText: string = this.i18N.tr('vessel-type');
    public vesselTypeDescription: string = "";

    public clearVesselTypeFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselTypes;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselTypeDescription = "";
        this.vesselTypeDisplayText = this.i18N.tr('vessel-type');
        if(reload)
            this.reloadFilters();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselTypes", "mclFilters.RS_VesselTypes.length")
    public get vesselTypeFilterActive() {
        const lookup = LookupValueQuery.VesselTypes;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    //#region vessel status filter
    public vesselStatusDisplayText: string = this.i18N.tr('vessel-status-text');
    public vesselStatusDescription: string = "";

    public clearVesselStatusFilter(reload: boolean = true) {
        const lookup = LookupValueQuery.VesselStatuses;
        if (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1)
            this.mclFilters[lookup] = [];
        this.vesselStatusDescription = "";
        this.vesselStatusDisplayText = this.i18N.tr('vessel-status-text');
        if(reload)
            this.reloadFilters();
    }

    @computedFrom("mclFilters", "mclFilters.RS_VesselStatuses", "mclFilters.RS_VesselStatuses.length")
    public get vesselStatusFilterActive() {
        const lookup = LookupValueQuery.VesselStatuses;
        return (this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0);
    }
    //#endregion

    openMCLDialog = (type: string) => {
        let lookup = null;
        if(type == "vessel_types")
            lookup = LookupValueQuery.VesselTypes;
        else if(type == "vessel_statuses")
            lookup = LookupValueQuery.VesselStatuses;
        else
        {
            console.error("invalid multi filter type")
        }
        const displayText = type == "vessel_types"? this.i18N.tr('vessel-type'): this.i18N.tr('vessel-status-text');

        this.closeAllFilters();

        const cachedMultiLookups = this.cachedMultipleLookups[lookup] ? this.cachedMultipleLookups[lookup] : RightShipMultiLookupModels.getMultiLookupModel(lookup);

        let filterValues: { [filterProperty: string]: number[] } = null;
        if(this.mclFilters && Object.keys(this.mclFilters).indexOf(lookup) > -1 && this.mclFilters[lookup] && this.mclFilters[lookup].length > 0)
        {
            filterValues = {};
            this.mclFilters[lookup].forEach(x => { 
                filterValues[x.filterProperty.name] = x.filterValue;
            });
        }

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MultiCheckedListDialog, {
            multiLookups: cachedMultiLookups,
            title: displayText,
            selectedIds: filterValues,
            onApply: (multiLookups: MultiLookupModel[]) => {

                this.mclFilters[lookup] = [];

                const result = RightShipMultiLookupModels.getMultiLookupFilters(lookup, displayText, multiLookups);

                this.mclFilters[lookup] = result.filters;

                if(lookup == LookupValueQuery.VesselTypes)
                {
                    this.vesselTypeDisplayText = result.filterDisplayText;
                    this.vesselTypeDescription = result.filterDescription;
                }
                if(lookup == LookupValueQuery.VesselStatuses)
                {
                    this.vesselStatusDisplayText = result.filterDisplayText;
                    this.vesselStatusDescription = result.filterDescription;
                }

                this.cachedMultipleLookups[lookup] = multiLookups;

                this.reloadFilters();
            }
        }, true, false, false, 'right');
    }
    //#endregion

    //#region dwt filter
    public dwtFirstFilter: number;
    public dwtSecondFilter: number;
    public mobileDwtFilter: { low: number, high: number };
    public dwtDefaults: { low: number, high: number } = {low: 0, high: 565000};
    public showDwtFilter: boolean = false;
    public dwtIsApplied: boolean = false;

    public toggleDwtFilters() {
        if (!this.showDwtFilter) {
            this.closeAllFilters();
        }
        this.showDwtFilter = !this.showDwtFilter;
        this.selectedFilter = {
            title: "DWT",
            clear: () => {
                this.clearDwtFilter();
            },
            reset: () => {
                this.mobileDwtFilter = RangeSelectorHelper.resetRangeFilter(this.dwtFirstFilter, this.dwtSecondFilter, this.dwtDefaults);
            },
            apply: () => {
                this.applyDwtFilter(this.mobileDwtFilter);
            }
        }
    }

    public applyDwtFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.dwtFirstFilter = filterValue.low;
            this.dwtSecondFilter = filterValue.high;
            this.dwtIsApplied = true;
        }

        this.reloadFilters();
        this.toggleDwtFilters();
    }

    public clearDwtFilter() {
        this.dwtFirstFilter = null;
        this.dwtSecondFilter = null;
        this.mobileDwtFilter = { low: null, high: null };
        this.dwtIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("dwtIsApplied", "dwtFirstFilter", "dwtSecondFilter")
    public get dwtFilterActive() {
        return this.dwtIsApplied && ((this.dwtFirstFilter && this.dwtFirstFilter >= this.dwtDefaults.low) || (this.dwtSecondFilter && this.dwtSecondFilter <= this.dwtDefaults.high));
    }

    //#endregion

    //#region insp filter
    public inspFirstFilter: number;
    public inspSecondFilter: number;
    public mobileInspFilter: { low: number, high: number };
    public inspDefaults: { low: number, high: number } = {low: 0, high: 500};
    public showInspFilter: boolean = false;
    public inspIsApplied: boolean = false;

    public toggleInspFilters() {
        if (!this.showInspFilter) {
            this.closeAllFilters();
        }
        this.showInspFilter = !this.showInspFilter;
        this.selectedFilter = {
            title: "Insp",
            clear: () => {
                this.clearInspFilter();
            },
            reset: () => {
                this.mobileInspFilter = RangeSelectorHelper.resetRangeFilter(this.inspFirstFilter, this.inspSecondFilter, this.inspDefaults);
            },
            apply: () => {
                this.applyInspFilter(this.mobileInspFilter);
            }
        }
    }

    public applyInspFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.inspFirstFilter = filterValue.low;
            this.inspSecondFilter = filterValue.high;
            this.inspIsApplied = true;
        }

        this.reloadFilters();
        this.toggleInspFilters();
    }

    public clearInspFilter() {
        this.inspFirstFilter = null;
        this.inspSecondFilter = null;
        this.mobileInspFilter = { low: null, high: null };
        this.inspIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("inspIsApplied", "inspFirstFilter", "inspSecondFilter")
    public get inspFilterActive() {
        return this.inspIsApplied && ((this.inspFirstFilter && this.inspFirstFilter >= this.inspDefaults.low) || (this.inspSecondFilter && this.inspSecondFilter <= this.inspDefaults.high));
    }

    //#endregion


    //#region det filter
    public detFirstFilter: number;
    public detSecondFilter: number;
    public mobileDetFilter: { low: number, high: number };
    public detDefaults: { low: number, high: number } = {low: 0, high: 500};
    public showDetFilter: boolean = false;
    public detIsApplied: boolean = false;

    public toggleDetFilters() {
        if (!this.showDetFilter) {
            this.closeAllFilters();
        }
        this.showDetFilter = !this.showDetFilter;
        this.selectedFilter = {
            title: "Det",
            clear: () => {
                this.clearDetFilter();
            },
            reset: () => {
                this.mobileDetFilter = RangeSelectorHelper.resetRangeFilter(this.detFirstFilter, this.detSecondFilter, this.detDefaults);
            },
            apply: () => {
                this.applyDetFilter(this.mobileDetFilter);
            }
        }
    }

    public applyDetFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.detFirstFilter = filterValue.low;
            this.detSecondFilter = filterValue.high;
            this.detIsApplied = true;
        }
        this.reloadFilters();
        this.toggleDetFilters();
    }

    public clearDetFilter() {
        this.detFirstFilter = null;
        this.detSecondFilter = null;
        this.mobileDetFilter = { low: null, high: null };
        this.detIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("detIsApplied", "detFirstFilter", "detSecondFilter")
    public get detFilterActive() {
        return this.detIsApplied && ((this.detFirstFilter && this.detFirstFilter >= this.detDefaults.low) || (this.detSecondFilter && this.detSecondFilter <= this.detDefaults.high));
    }

    //#endregion

    //#region defs filter
    public defsFirstFilter: number;
    public defsSecondFilter: number;
    public mobileDefsFilter: { low: number, high: number };
    public defsDefaults: { low: number, high: number } = {low: 0, high: 500};
    public showDefsFilter: boolean = false;
    public defsIsApplied: boolean = false;

    public toggleDefsFilters() {
        if (!this.showDefsFilter) {
            this.closeAllFilters();
        }
        this.showDefsFilter = !this.showDefsFilter;
        this.selectedFilter = {
            title: "Defs",
            clear: () => {
                this.clearDefsFilter();
            },
            reset: () => {
                this.mobileDefsFilter = RangeSelectorHelper.resetRangeFilter(this.defsFirstFilter, this.defsSecondFilter, this.defsDefaults);
            },
            apply: () => {
                this.applyDefsFilter(this.mobileDefsFilter);
            }
        }
    }

    public applyDefsFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.defsFirstFilter = filterValue.low;
            this.defsSecondFilter = filterValue.high;
            this.defsIsApplied = true;
        }
        this.reloadFilters();
        this.toggleDefsFilters();
    }

    public clearDefsFilter() {
        this.defsFirstFilter = null;
        this.defsSecondFilter = null;
        this.mobileDefsFilter = { low: null, high: null };
        this.defsIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("defsIsApplied", "defsFirstFilter", "defsSecondFilter")
    public get defsFilterActive() {
        return this.defsIsApplied && ((this.defsFirstFilter && this.defsFirstFilter >= this.defsDefaults.low) || (this.defsSecondFilter && this.defsSecondFilter <= this.defsDefaults.high));
    }

    //#endregion

    //#region avgDefInsp filter
    public avgDefInspFirstFilter: number;
    public avgDefInspSecondFilter: number;
    public mobileAvgDefInspFilter: { low: number, high: number };
    public avgDefInspDefaults: { low: number, high: number } = {low: 0, high: 500};
    public showAvgDefInspFilter: boolean = false;
    public avgDefInspIsApplied: boolean = false;

    public toggleAvgDefInspFilters() {
        if (!this.showAvgDefInspFilter) {
            this.closeAllFilters();
        }
        this.showAvgDefInspFilter = !this.showAvgDefInspFilter;
        this.selectedFilter = {
            title: "AvgDefInsp",
            clear: () => {
                this.clearAvgDefInspFilter();
            },
            reset: () => {
                this.mobileAvgDefInspFilter = RangeSelectorHelper.resetRangeFilter(this.avgDefInspFirstFilter, this.avgDefInspSecondFilter, this.avgDefInspDefaults);
            },
            apply: () => {
                this.applyAvgDefInspFilter(this.mobileAvgDefInspFilter);
            }
        }
    }

    public applyAvgDefInspFilter(filterValue: { low: number, high: number } = null) {
        if (filterValue) {
            this.avgDefInspFirstFilter = filterValue.low;
            this.avgDefInspSecondFilter = filterValue.high;
            this.avgDefInspIsApplied = true;
        }
        this.reloadFilters();
        this.toggleAvgDefInspFilters();
    }

    public clearAvgDefInspFilter() {
        this.avgDefInspFirstFilter = null;
        this.avgDefInspSecondFilter = null;
        this.mobileAvgDefInspFilter = { low: null, high: null };
        this.avgDefInspIsApplied = false;
        this.reloadFilters();
    }

    @computedFrom("avgDefInspIsApplied", "avgDefInspFirstFilter", "avgDefInspSecondFilter")
    public get avgDefInspFilterActive() {
        return this.avgDefInspIsApplied && ((this.avgDefInspFirstFilter && this.avgDefInspFirstFilter >= this.avgDefInspDefaults.low) || (this.avgDefInspSecondFilter && this.avgDefInspSecondFilter <= this.avgDefInspDefaults.high));
    }

    //#endregion

    //#region psc date rating
    public selectedPscDates: number[] = [5];
    public mobilePscDates: number[] = [];
    public showPscDateFilter: boolean = false;
    public pscDates =
        [
            { id: 1, name: '12 months', months: 12 },
            { id: 2, name: '24 months', months: 24 },
            { id: 3, name: '36 months', months: 36 },
            { id: 4, name: '48 months', months: 48 },
            { id: 5, name: '60 months', months: 60 }
        ]

    public togglePscDateFilters() {
        if (!this.showPscDateFilter) {
            this.closeAllFilters();
        }
        this.showPscDateFilter = !this.showPscDateFilter;
        this.selectedFilter = {
            title: "PSC Date",
            clear: () => {
                this.clearPscDateFilter();
            },
            reset: () => {
                this.mobilePscDates = CloneHelper.cloneObject(this.selectedPscDates);
            },
            apply: () => {
                this.applyPscDates(this.mobilePscDates.map(r => { return { id: r }; }));
            }
        }
    }

    public applyPscDates(items: any[]) {
        this.selectedPscDates = items.map(x => x.id);
        this.reloadFilters();
        this.togglePscDateFilters();
    }

    public clearPscDateFilter() {
        this.selectedPscDates = [];
        this.mobilePscDates = [];
        this.reloadFilters();
    }

    @computedFrom("selectedPscDates", "selectedPscDates.length")
    public get pscDateFilterActive() {
        return this.selectedPscDates && this.selectedPscDates.length > 0;
    }

    //#endregion

    //#region roles filter
    public selectedRoles: number[] = [];
    public mobileRoles: number[] = [];
    public showRoleFilter: boolean = false;
    public roles =
        [
            { id: 1, name: 'Beneficial Owner (BO)', prop: 'isBeneficialOwnerCompany' },
            { id: 2, name: 'Commercial Manager (CM)', prop: 'isCommercialManagerCompany' },
            { id: 3, name: 'Document of Compliance (DOC)', prop: 'isDocumentOfComplianceCompany' },
            { id: 4, name: 'Commerical Owner (CO)', prop: 'isCommericalOwnerCompany' },
            { id: 5, name: 'Registered Owner (RO)', prop: 'isRegisteredOwnerCompany' },
            { id: 6, name: 'Technical Manager (TM)', prop: 'isTechnicalManagerCompany' }
        ]

    public toggleRoleFilters() {
        if (!this.showRoleFilter) {
            this.closeAllFilters();
        }
        this.showRoleFilter = !this.showRoleFilter;
        this.selectedFilter = {
            title: "Roles",
            clear: () => {
                this.clearRoleFilter();
            },
            reset: () => {
                this.mobileRoles = CloneHelper.cloneObject(this.selectedRoles);
            },
            apply: () => {
                this.applyRoles(this.mobileRoles.map(r => { return { id: r }; }));
            }
        }
    }

    public applyRoles(items: any[]) {
        this.selectedRoles = items.map(x => x.id);
        this.reloadFilters();
        this.toggleRoleFilters();
    }

    public clearRoleFilter() {
        this.selectedRoles = [];
        this.mobileRoles = [];
        this.reloadFilters();
    }

    @computedFrom("selectedRoles", "selectedRoles.length")
    public get roleFilterActive() {
        return this.selectedRoles && this.selectedRoles.length > 0;
    }

    //#endregion

    //#region mobile filters
    isMobileFilterOpen: boolean = false;

    toggleMobileFilterDialog() {
        this.isMobileFilterOpen = !this.isMobileFilterOpen;
    }

    //#endregion

    public age(item: any) {
        if (item) {
            if (item.dateOfBuild) {
                return VesselHelper.getAgeFromDate(item.dateOfBuild);
            } else {
                return ""
            }
        } else {
            return null;
        }
    }
}
